import React, { useMemo, useState } from 'react';
import { addStudentMemos } from '../../api/classRosterApi';
import { useToast } from '../../components/Toast';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import { PASTE_ISSUE_LABELS, parsePastedMemos, pasteClientId } from '../../utils/memoQuick';
import { MEMO_MAX_CHARS, suggestMemoArea } from '../../utils/studentMemos';
import ClassSheet from './ClassSheet';
import { DateChips } from './MemoSheet';

const CHUNK = 300;
const FIXABLE = new Set(['no_student', 'mismatch', 'ambiguous']);

/** 여러 줄을 붙여넣으면 번호·이름으로 학생별 메모로 나눈다. 못 맞춘 줄은 학생을 골라 주거나 빼고 남긴다. */
const MemoPasteSheet = ({ roster, students, day, onDayChange, onClose, onSaved }) => {
  // 고른 학생은 줄 순서·내용을 열쇠로 기억한다(위 줄을 고치면 다시 골라야 한다).
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => parsePastedMemos(text, students, { defaultDate: day, schoolYear: roster.school_year }), [text, students, day, roster.school_year]);
  const resolved = rows.map((row, index) => {
    const key = `${index}:${row.line}`;
    const fixable = FIXABLE.has(row.issue) && Boolean(row.text);
    const sid = row.sid || (fixable && !row.dateIssue ? picked[key] : '');
    const student = students.find(item => item.sid === sid) || null;
    return { ...row, sid: student?.sid || null, student, key, fixable, issue: fixable && row.dateIssue ? row.dateIssue : row.issue };
  });
  const ready = resolved.filter(row => row.sid && row.text && row.text.length <= MEMO_MAX_CHARS);
  const flagged = resolved.length - ready.length;

  // 줄마다 client_id가 내용으로 정해져(pasteClientId) 다시 눌러도, 닫았다 다시 붙여도 서버에서 겹치지 않는다.
  // 이번 시트에서 이미 남긴 줄은 다시 보내지도 않는다.
  const [done, setDone] = useState(() => new Set());
  const seen = new Map();
  const items = ready.map((row) => {
    const base = `${row.sid}|${row.date}|${row.text}`;
    const nth = (seen.get(base) || 0) + 1;
    seen.set(base, nth);
    return {
      identity: `${base}#${nth}`,
      sid: row.sid, text: row.text, area: suggestMemoArea(row.text), date: row.date,
      clientId: pasteClientId({ rosterId: roster.id, sid: row.sid, date: row.date, text: row.text, nth }),
    };
  });
  const pending = items.filter(item => !done.has(item.identity));

  const save = async () => {
    if (!pending.length || saving) return;
    setSaving(true);
    const saved = [];
    try {
      for (let start = 0; start < pending.length; start += CHUNK) {
        const chunk = pending.slice(start, start + CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const memos = await addStudentMemos(roster.id, chunk.map(({ identity, ...item }) => item), 'paste');
        saved.push(...memos);
        setDone(prev => new Set([...prev, ...chunk.map(item => item.identity)]));
      }
      onSaved(saved);
      trackClassRosterEvent('class_memo_paste_saved', {
        roster_id: roster.id, line_count: resolved.length, memo_count: saved.length, unmatched_count: flagged,
      });
      showToast(`메모 ${saved.length}개를 남겼어요`, 'success');
      onClose();
    } catch (error) {
      if (saved.length) onSaved(saved);
      showToast(error.message || '메모를 남기지 못했어요. 다시 시도해 주세요.', 'error');
      setSaving(false);
    }
  };

  return (
    <ClassSheet
      title="붙여넣기로 한 번에"
      subtitle="한 줄에 한 학생 · 번호나 이름으로 시작"
      onClose={onClose}
      testId="class-memo-paste-sheet"
      footer={(
        <button type="button" className="classes-btn-primary class-sheet-wide" disabled={!pending.length || saving} onClick={save}>
          {saving ? '남기는 중…' : pending.length ? `${done.size ? '남은 ' : ''}${pending.length}개 남기기` : '남길 줄이 없어요'}
        </button>
      )}
    >
      <DateChips day={day} onChange={onDayChange} schoolYear={roster.school_year} />
      <textarea
        className="class-memo-paste-input"
        aria-label="붙여넣을 메모"
        rows={5}
        value={text}
        placeholder={'3 김철수 발표 때 근거를 들어 설명함\n10/2 김하늘 모둠 정리를 맡음'}
        onChange={event => setText(event.target.value)}
      />
      {resolved.length > 0 && (
        <>
          <p className="class-memo-paste-count" aria-live="polite">
            {ready.length}줄 맞췄어요{flagged > 0 && <strong> · {flagged}줄 확인</strong>}
          </p>
          <ul className="class-memo-paste-preview" data-testid="class-memo-paste-preview">
            {resolved.map(row => (
              <li key={row.key} className={row.sid ? '' : 'is-flagged'}>
                {row.fixable && !row.dateIssue ? (
                  <select
                    aria-label={`학생 고르기: ${row.line}`}
                    value={picked[row.key] || ''}
                    onChange={event => setPicked(prev => ({ ...prev, [row.key]: event.target.value }))}
                  >
                    <option value="">학생 고르기</option>
                    {students.map(item => <option key={item.sid} value={item.sid}>{item.number} {item.name}</option>)}
                  </select>
                ) : row.sid ? (
                  <span className="class-memo-paste-who">{row.student.number} {row.student.name}</span>
                ) : (
                  <span className="class-memo-paste-who is-empty">빼고 남겨요</span>
                )}
                <span className="class-memo-paste-text">{row.sid || row.fixable ? row.text : row.line}</span>
                {row.sid && row.date !== day && <span className="class-memo-saved-day">{row.date.slice(5).replace('-', '/')}</span>}
                {!row.sid && <span className="class-memo-paste-issue">{PASTE_ISSUE_LABELS[row.issue]}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </ClassSheet>
  );
};

export default MemoPasteSheet;
